import { Component } from '@theme/component';

export default class RetailersTabs extends Component {
  constructor() {
    super();
    /** @type {Set<string>} */
    this.activeFilters = new Set();
    /** @type {() => void} */
    this._onHashChange = () => this.restoreTabFromHash();
  }

  connectedCallback() {
    super.connectedCallback();
    this.filterRetailers();
    this.restoreTabFromHash();
    window.addEventListener('hashchange', this._onHashChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('hashchange', this._onHashChange);
  }

  /** Read location.hash and activate the matching tab if valid. */
  restoreTabFromHash() {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    // Only act on known tab ids
    const validTabs = this.getValidTabIds();
    if (validTabs.has(hash)) {
      this.activateTab(hash);
    }
  }

  /** @returns {Set<string>} */
  getValidTabIds() {
    const ids = new Set();
    const btns = this.refs.tabBtns
      ? (Array.isArray(this.refs.tabBtns) ? this.refs.tabBtns : [this.refs.tabBtns])
      : [];
    btns.forEach(btn => {
      if (/** @type {HTMLElement} */(btn).dataset.tab) {
        ids.add(/** @type {HTMLElement} */(btn).dataset.tab);
      }
    });
    return ids;
  }

  /**
   * Activate a tab by its id without updating the hash (to avoid loops).
   * @param {string} tabId
   */
  activateTab(tabId) {
    if (this.refs.tabBtns) {
      const btns = Array.isArray(this.refs.tabBtns) ? this.refs.tabBtns : [this.refs.tabBtns];
      btns.forEach(btn => {
        /** @type {HTMLElement} */
        const el = (btn);
        el.classList.toggle('active', el.dataset.tab === tabId);
      });
    }
    if (this.refs.tabContents) {
      const contents = Array.isArray(this.refs.tabContents) ? this.refs.tabContents : [this.refs.tabContents];
      contents.forEach(content => {
        /** @type {HTMLElement} */
        const el = (content);
        if (el.dataset.tabContent === tabId) {
          el.classList.add('active');
          el.classList.remove('hidden');
        } else {
          el.classList.remove('active');
          el.classList.add('hidden');
        }
      });
    }
  }

  /** @param {Event} event */
  switchTab(event) {
    /** @type {HTMLElement} */
    const target = (event.target);
    const selectedTab = target.dataset.tab;
    if (!selectedTab) return;

    this.activateTab(selectedTab);
    // Update location hash (replaceState to avoid polluting history on every click)
    history.replaceState(null, '', `#${selectedTab}`);
  }

  /** @param {Event} event */
  handleFilterChange(event) {
    /** @type {HTMLInputElement} */
    const checkbox = (event.target);
    const value = checkbox.value.toLowerCase();
    
    if (checkbox.checked) {
      this.activeFilters.add(value);
    } else {
      this.activeFilters.delete(value);
    }
    this.filterRetailers();
  }

  filterRetailers() {
    const showAll = this.activeFilters.size === 0;
    const items = this.querySelectorAll('.retailer-item');
    
    items.forEach(item => {
      /** @type {HTMLElement} */
      const element = (item);
      if (!element.dataset.tags) {
        element.style.display = showAll ? 'block' : 'none';
        return;
      }
      
      const itemTags = element.dataset.tags.split(',').map((/** @type {string} */ tag) => tag.trim());
      // Show if no filters selected, OR if the item has ANY of the selected filters
      const hasAny = showAll || Array.from(this.activeFilters).some(filter => itemTags.includes(filter));
      
      element.style.display = hasAny ? 'block' : 'none';
    });

    // Subregion (Tier 2) visibility logic
    const tier1Items = this.querySelectorAll('.locator-list-tier1-item');
    tier1Items.forEach(group => {
      const retailerItems = Array.from(group.querySelectorAll('.retailer-item'));
      const hasVisibleRetailers = retailerItems.some(item => /** @type {HTMLElement} */(item).style.display !== 'none');
      
      /** @type {HTMLElement} */
      const groupElement = (group);
      groupElement.style.display = hasVisibleRetailers ? '' : 'none';
    });

    // Column visibility logic
    const columns = this.querySelectorAll('.locator-column');
    columns.forEach(col => {
      const subregions = Array.from(col.querySelectorAll('.locator-list-tier1-item'));
      const hasVisibleSubregions = subregions.some(sub => /** @type {HTMLElement} */(sub).style.display !== 'none');
      
      /** @type {HTMLElement} */
      const colElement = (col);
      colElement.style.display = hasVisibleSubregions ? '' : 'none';
    });

    // Region (Tier 1) visibility logic
    const tier1Groups = this.querySelectorAll('.locator-list-tier1');
    tier1Groups.forEach(tier1List => {
      const subregions = Array.from(tier1List.querySelectorAll('.locator-list-tier1-item'));
      const hasVisibleSubregions = subregions.some(sub => /** @type {HTMLElement} */(sub).style.display !== 'none');
      
      /** @type {HTMLElement | null} */
      const parentElement = (tier1List.parentElement);
      if (parentElement) {
        parentElement.style.display = hasVisibleSubregions ? '' : 'none';
      }
    });
  }

  /** @param {Event} event */
  toggleTier1(event) {
    /** @type {HTMLElement} */
    const btn = (event.target);
    const content = btn.parentElement?.nextElementSibling;
    const icon = btn.querySelector('.icon-indicator');
    if (content) {
      content.classList.toggle('hidden');
      if (icon) icon.textContent = content.classList.contains('hidden') ? '+' : '−';
    }
  }

  /** @param {Event} event */
  toggleTier2(event) {
    /** @type {HTMLElement} */
    const btn = (event.target);
    const content = btn.parentElement?.nextElementSibling;
    const icon = btn.querySelector('.icon-indicator');
    if (content) {
      content.classList.toggle('hidden');
      if (icon) icon.textContent = content.classList.contains('hidden') ? '+' : '−';
    }
  }
}

customElements.define('retailers-tabs', RetailersTabs);
